import { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { fetchFeedHealth } from '@src/api/feedHealth';
import { useMessage } from '@src/context/common';

dayjs.extend(relativeTime);

const FEED_STATE = {
  healthy: { color: 'success', label: 'Receiving' },
  overdue: { color: 'warning', label: 'Overdue' },
  degraded: { color: 'warning', label: 'Degraded' },
  failed: { color: 'error', label: 'Failed' },
  pending: { color: 'default', label: 'Pending' },
  disabled: { color: 'default', label: 'Disabled' },
};

// mapping_type -> the task family that runs it (deep-link into By job / All runs).
const FEED_TASK_FAMILY = {
  email: 'process_email_mappings',
  sftp: 'process_sftp_mappings',
  sharepoint: 'process_sharepoint_mappings',
  api: 'process_api_mappings',
  google_sheet: 'process_google_sheet_mappings',
};

function relTime(value) {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.fromNow() : '—';
}

function StateChip({ state }) {
  const meta = FEED_STATE[state] || { color: 'default', label: state };
  return <Chip size="small" color={meta.color} label={meta.label} />;
}

export default function FeedsTab({ onDrillToRuns }) {
  const message = useMessage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [problemsOnly, setProblemsOnly] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async (only = problemsOnly) => {
    setLoading(true);
    try {
      const res = await fetchFeedHealth({ problems_only: only ? 'true' : 'false' });
      setData(res?.data || null);
    } catch (error) {
      console.error('Error loading feed health', error);
      message?.error?.('Failed to load feed health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(problemsOnly);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemsOnly]);

  // Group feed rows by client (parent-scoped feeds get their own group).
  const groups = useMemo(() => {
    const feeds = data?.feeds || [];
    const map = new Map();
    feeds.forEach((row) => {
      const key = row.client_id != null ? `c:${row.client_id}` : `p:${row.client_name}`;
      if (!map.has(key)) {
        map.set(key, { key, name: row.client_name, clientId: row.client_id, rows: [] });
      }
      map.get(key).rows.push(row);
    });
    return Array.from(map.values());
  }, [data]);

  const backendFor = (clientId) => (clientId != null ? data?.backend?.[clientId] : null);
  const reconFor = (clientId) => (clientId != null ? data?.recon?.[clientId] : null);

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <FormControlLabel
          control={<Switch checked={problemsOnly} onChange={(e) => setProblemsOnly(e.target.checked)} />}
          label="Problems only"
        />
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => load(problemsOnly)}>
          Refresh
        </Button>
      </Stack>

      {loading && !data ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress size={24} />
        </Stack>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" align="center" py={3}>
              {problemsOnly ? 'No feed problems right now. 🎉' : 'No feeds found.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        groups.map((group) => {
          const backend = backendFor(group.clientId);
          const recon = reconFor(group.clientId);
          return (
            <Card key={group.key}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" mb={1}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {group.name}
                  </Typography>
                  {backend && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={backend.backend_stale ? 'warning' : 'success'}
                      label={
                        backend.backend_max_date
                          ? `Backend data: ${backend.backend_max_date}${
                              backend.backend_stale ? ` (stale ${backend.days_stale ?? '?'}d)` : ''
                            }`
                          : 'Backend data: none yet'
                      }
                    />
                  )}
                  {recon && (
                    <Tooltip title={recon.detail || 'Accepted leads missing from backend export'}>
                      <Chip size="small" color="error" label="Reconciliation gap" />
                    </Tooltip>
                  )}
                </Stack>
                <Divider sx={{ mb: 1 }} />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Feed</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Last report</TableCell>
                        <TableCell>Diagnosis</TableCell>
                        <TableCell align="right" width={80} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.rows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <Typography variant="body2">{row.source_label}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                              {row.label}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StateChip state={row.state} />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{relTime(row.last_report_at)}</TableCell>
                          <TableCell sx={{ maxWidth: 360 }}>
                            <Typography variant="body2">{row.reason}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" onClick={() => setSelected(row)}>
                              Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          );
        })
      )}
      {data?.generated_at && (
        <Typography variant="caption" color="text.secondary">
          Updated {relTime(data.generated_at)}
        </Typography>
      )}

      <Drawer anchor="right" open={Boolean(selected)} onClose={() => setSelected(null)}>
        <Box sx={{ width: { xs: 320, sm: 440 }, p: 2 }}>
          {selected && (
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h6">{selected.source_label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selected.client_name}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setSelected(null)}>
                  <CloseIcon />
                </IconButton>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <StateChip state={selected.state} />
                {selected.last_run_status && (
                  <Chip size="small" variant="outlined" label={`run: ${selected.last_run_status}`} />
                )}
              </Stack>

              <Typography variant="body2">{selected.reason}</Typography>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  What we're looking for
                </Typography>
                <Table size="small">
                  <TableBody>
                    {Object.entries(selected.criteria || {}).map(([k, v]) => (
                      <TableRow key={k}>
                        <TableCell sx={{ fontWeight: 600, width: 140, border: 0, py: 0.25 }}>{k}</TableCell>
                        <TableCell sx={{ border: 0, py: 0.25, wordBreak: 'break-all' }}>
                          {String(v === '' || v === null || v === undefined ? '—' : v)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Last report
                  </Typography>
                  <Typography variant="body2">{relTime(selected.last_report_at)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Last import
                  </Typography>
                  <Typography variant="body2">{relTime(selected.last_ingest_at)}</Typography>
                </Box>
              </Stack>

              {selected.last_run_summary && Object.keys(selected.last_run_summary).length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Last run summary
                  </Typography>
                  <Box
                    component="pre"
                    sx={(theme) => ({
                      m: 0,
                      p: 1.5,
                      fontSize: 12,
                      borderRadius: 1,
                      overflowX: 'auto',
                      bgcolor: theme.palette.action.hover,
                    })}
                  >
                    {JSON.stringify(selected.last_run_summary, null, 2)}
                  </Box>
                </Box>
              )}

              {onDrillToRuns && FEED_TASK_FAMILY[selected.mapping_type] && (
                <Link
                  component="button"
                  onClick={() => {
                    onDrillToRuns(FEED_TASK_FAMILY[selected.mapping_type]);
                    setSelected(null);
                  }}
                >
                  View this feed's task runs →
                </Link>
              )}
            </Stack>
          )}
        </Box>
      </Drawer>
    </Stack>
  );
}
